import { EMPTY_GUID, tempuserGuid } from "@/utils/constant";
import {
  createCookie,
  normalizeGuid,
  readCookie,
  stripGuidHyphens,
} from "@/utils/helper";
import { getVisitGuidForTracking, getVisitorGuidForTracking } from "./cookieIdentity";

/** Portal attribution for SongSimpli (visitor.js getPortal). */
export const SONGSIMPLI_PORTAL_CODE = "PSSG";

export function isSongSimpliHost(): boolean {
  return (
    typeof window !== "undefined" &&
    window.location.hostname.toLowerCase().includes("songsimpli")
  );
}

const SONGSIMPLI_META = {
  title: "Customize Your Song Details, Rhythm etc | SongSimpli Song Maker",
  description:
    "Build a personalized song tailored to your story. Add your relationship details, favorite memories, and preferred rhythm to create the ultimate custom track.",
  favicon:
    "https://static-test.songsimpli.com/wp-content/uploads/images-redesign/fav-icon.png",
};

/** index.html ships PDFSimpli meta/favicon by default; swap to SongSimpli's on that host. */
export function applyDomainMetaTags(): void {
  if (typeof document === "undefined" || !isSongSimpliHost()) return;

  document.title = SONGSIMPLI_META.title;

  const descriptionTag = document.querySelector('meta[name="description"]');
  if (descriptionTag) {
    descriptionTag.setAttribute("content", SONGSIMPLI_META.description);
  }

  const faviconTag = document.querySelector('link[rel="icon"]');
  if (faviconTag) {
    faviconTag.setAttribute("href", SONGSIMPLI_META.favicon);
  }
}

/** Sets prtlSrc on SongSimpli so visitor API posts the correct portal. */
export function ensureSongSimpliPortalCookies(): void {
  if (!isSongSimpliHost()) return;
  const existing = readCookie("prtlSrc") || readCookie("vs_prtlSrc");
  if (existing) return;
  createCookie("prtlSrc", SONGSIMPLI_PORTAL_CODE, 1);
  createCookie("vs_prtlSrc", SONGSIMPLI_PORTAL_CODE, 1);
}

/** Mirrors PDFSimpli Login hydrateVisitorHandoffFromQuery. */
export function hydrateVisitorCookiesFromQuery(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const vstr = params.get("vstr");
  const vst = params.get("vst");
  const prtlSrc = params.get("prtlSrc") || params.get("prtlsrc");
  if (vstr) {
    createCookie("visitorGuid", vstr, 365);
  }
  if (vst) {
    createCookie("visitGuid", vst);
  }
  if (prtlSrc) {
    createCookie("prtlSrc", prtlSrc, 1);
    createCookie("vs_prtlSrc", prtlSrc, 1);
  }
}

function resolveHandoffUserId(): string {
  const fromCookie = readCookie("userguid");
  if (fromCookie) {
    const normalized = normalizeGuid(fromCookie);
    if (normalized) {
      return stripGuidHyphens(normalized);
    }
  }
  const pSi = readCookie("p_si");
  if (pSi) {
    return stripGuidHyphens(pSi);
  }
  return tempuserGuid;
}

/** OpenPDF-style ids for PDFSimpli signup/signin (vstr, vst, prtlSrc, uid). */
export function appendPdfSimpliVisitorHandoffParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  ensureSongSimpliPortalCookies();

  const visitor = getVisitorGuidForTracking();
  const visit = getVisitGuidForTracking();
  const prtlSrc =
    readCookie("prtlSrc") ||
    readCookie("vs_prtlSrc") ||
    (isSongSimpliHost() ? SONGSIMPLI_PORTAL_CODE : "");

  if (visitor && visitor !== EMPTY_GUID) {
    searchParams.set("vstr", visitor);
  }
  if (visit && visit !== EMPTY_GUID) {
    searchParams.set("vst", visit);
  }
  if (prtlSrc) {
    searchParams.set("prtlSrc", prtlSrc);
  }
  searchParams.set("uid", resolveHandoffUserId());
  return searchParams;
}

export function buildPdfSimpliAuthRedirectQuery(
  baseParams: Record<string, string | null | undefined>,
): string {
  const searchParams = new URLSearchParams();
  Object.entries(baseParams).forEach(([key, value]) => {
    if (value != null && value !== "") {
      searchParams.set(key, value);
    }
  });
  appendPdfSimpliVisitorHandoffParams(searchParams);
  return searchParams.toString();
}
